// Services
import ControlTowerService from 'src/services/controlTower'

// Utils
import {
  emptyFilters,
  fromSavedFilter,
  pinColumn,
  resolveListColumns,
  sendColumnToEnd,
  storedStateFor,
  swapColumns,
  toRequestPayload,
  toSavedFilterPayload
} from '@/utils/controlTower'

/**
 * Todas las cargas pasan por aquí y todas actualizan `status`, que es lo que
 * pinta la pantalla de carga / vacío / error del diseño.
 */
export default {
  // --- Configuración de vista ---------------------------------------------

  async setViewType (viewType) {
    if (this.viewType === viewType) return

    this.viewType = viewType
    this.listColumns = resolveListColumns(this.listColumns, viewType)
    this.clearSelection()
    this.focusedColumnKey = null

    await this.loadPreferences()
    if (this.status !== 'idle') await this.search()
  },

  async setLayout (layout) {
    if (this.layout === layout) return

    this.layout = layout
    this.clearSelection()
    // El foco del kanban (una columna) no significa lo mismo que el de la
    // lista (un estatus): no se arrastra de un formato al otro.
    this.focusedColumnKey = null
    if (this.status !== 'idle') await this.search()
  },

  /**
   * Clic en un indicador. En kanban solo se muestra esa columna (se resuelve en
   * el getter `visibleColumns`). En lista hay que pedirle al servidor solo esas
   * tareas: la tabla está paginada y filtrar lo ya cargado dejaría fuera casi
   * todo. Los indicadores no se mueven: el resumen sigue siendo el de los
   * filtros, no el del foco.
   */
  async focusColumn (columnKey) {
    const next = this.focusedColumnKey === columnKey ? null : columnKey
    if (next === this.focusedColumnKey) return

    this.focusedColumnKey = next
    if (this.layout !== 'list' || this.status === 'idle') return

    this.clearSelection()
    try {
      await this.fetchList()
    } catch (error) {
      console.error(error)
    }
  },

  // --- Búsqueda ------------------------------------------------------------

  /**
   * Ejecuta la búsqueda con los filtros actuales. Es el único punto que mueve
   * `status`, para que la pantalla nunca quede en un estado inventado.
   */
  async search () {
    this.status = 'loading'
    this.error = null

    try {
      if (this.layout === 'list') {
        await this.fetchList()
      } else {
        await this.fetchBoard()
      }

      this.appliedFilters = JSON.parse(JSON.stringify(this.filters))
      this.status = this.isResultEmpty() ? 'empty' : 'ready'
    } catch (error) {
      console.error(error)
      this.error = error
      this.status = 'error'
    }
  },

  isResultEmpty () {
    if (this.layout === 'list') return (this.list.rows || []).length === 0
    return (this.board.columns || []).every(column => !column.counts || column.counts.total === 0)
  },

  async fetchBoard () {
    const payload = toRequestPayload(this.filters, {
      groupBy: this.viewType,
      columnPage: 1,
      columnPerPage: 12,
      tasksPerColumn: 20
    })

    const { data } = await ControlTowerService.fetchBoard(payload)

    this.board = {
      columns: data.columns || [],
      meta: data.columns_meta || null,
      summary: data.summary || null
    }
  },

  async fetchList () {
    const payload = toRequestPayload(this.filters, {
      page: 1,
      perPage: 50,
      sortBy: this.list.sortBy,
      sortDir: this.list.sortDir,
      focus: this.focusedColumnKey
    })

    const { data } = await ControlTowerService.fetchTaskList(payload)

    this.list = {
      ...this.list,
      rows: data.data || [],
      meta: data.meta || null,
      summary: data.summary || null
    }
  },

  /**
   * Siguiente página de una columna del kanban (scroll infinito por columna).
   */
  async loadMoreColumn (columnKey) {
    const column = this.board.columns.find(item => String(item.key) === String(columnKey))
    if (!column) return

    const page = Math.floor((column.tasks || []).length / 20) + 1
    const payload = toRequestPayload(this.filters, {
      groupBy: this.viewType,
      columnKey,
      page,
      perPage: 20
    })

    const { data } = await ControlTowerService.fetchColumnTasks(payload)
    column.tasks = [...(column.tasks || []), ...(data.data || [])]
  },

  async loadMoreListRows () {
    if (!this.list.meta || this.list.meta.page >= this.list.meta.last_page) return

    const payload = toRequestPayload(this.filters, {
      page: this.list.meta.page + 1,
      perPage: this.list.meta.per_page,
      sortBy: this.list.sortBy,
      sortDir: this.list.sortDir,
      focus: this.focusedColumnKey
    })

    const { data } = await ControlTowerService.fetchTaskList(payload)
    this.list.rows = [...this.list.rows, ...(data.data || [])]
    this.list.meta = data.meta
  },

  async sortList (sortBy) {
    this.list.sortDir = this.list.sortBy === sortBy && this.list.sortDir === 'asc' ? 'desc' : 'asc'
    this.list.sortBy = sortBy
    await this.search()
  },

  // --- Catálogos del panel de filtros --------------------------------------

  async fetchUsers ({ search = null, page = 1 } = {}) {
    this.users.loading = true
    try {
      const { data } = await ControlTowerService.fetchUsers({ search, page, perPage: 50 })
      this.users = {
        data: page === 1 ? data.data : [...this.users.data, ...data.data],
        meta: data.meta,
        loading: false
      }
    } catch (error) {
      console.error(error)
      this.users.loading = false
    }
  },

  async fetchOrganizationLocations ({ search = null, page = 1 } = {}) {
    try {
      const { data } = await ControlTowerService.fetchOrganizationLocations({ search, page, perPage: 100 })
      this.organizationLocations = {
        data: page === 1 ? data.data : [...this.organizationLocations.data, ...data.data],
        meta: data.meta
      }
    } catch (error) {
      console.error(error)
    }
  },

  async fetchCatalogs () {
    try {
      const [areas, categories] = await Promise.all([
        ControlTowerService.fetchAreas(),
        ControlTowerService.fetchCategories()
      ])
      this.areas = areas.data || []
      this.categories = categories.data || []
    } catch (error) {
      console.error(error)
    }
  },

  // --- Selección y acciones masivas ----------------------------------------

  toggleSelectionMode () {
    this.selection.active = !this.selection.active
    if (!this.selection.active) this.clearSelection()
  },

  clearSelection () {
    this.selection = { active: this.selection.active, columnKey: null, ids: [] }
  },

  /**
   * En kanban la selección es de una columna a la vez: cambiar de columna
   * reemplaza la selección anterior, como en el diseño.
   */
  toggleTaskSelection (task, columnKey = null) {
    if (!this.selection.active) return

    const sameColumn = this.layout === 'list' || this.selection.columnKey === null ||
      String(this.selection.columnKey) === String(columnKey)

    if (!sameColumn) {
      this.selection = { active: true, columnKey, ids: [task.id] }
      return
    }

    const ids = new Set(this.selection.ids)
    if (ids.has(task.id)) ids.delete(task.id)
    else ids.add(task.id)

    this.selection = {
      active: true,
      columnKey: this.layout === 'list' ? null : columnKey,
      ids: Array.from(ids)
    }
  },

  selectAllInList (checked) {
    this.selection = {
      active: true,
      columnKey: null,
      ids: checked ? this.list.rows.map(task => task.id) : []
    }
  },

  async bulkDelete () {
    const { data } = await ControlTowerService.bulkDelete({ ids: this.selection.ids })
    this.clearSelection()
    await this.search()
    return data
  },

  async bulkReassign (assignedTo) {
    const { data } = await ControlTowerService.bulkReassign({ ids: this.selection.ids, assigned_to: assignedTo })
    this.clearSelection()
    await this.search()
    return data
  },

  async bulkUpdate (payload) {
    const { data } = await ControlTowerService.bulkUpdate({ ids: this.selection.ids, ...payload })
    this.clearSelection()
    await this.search()
    return data
  },

  /**
   * Cambia el estatus de una o varias tareas sin pasar por "Seleccionar
   * varias": menú de estatus de la tarjeta y drag and drop de una sola tarea.
   * `state` es el valor que guarda el backend (`created`, `suspended`,
   * `finished`), no la clave de columna.
   */
  async updateTasksState (ids, state) {
    const { data } = await ControlTowerService.bulkUpdate({ ids, field: 'state', value: state })
    this.dropFromSelection(ids)
    await this.search()
    return data
  },

  /**
   * Compatibilidad: mover la selección vigente a la columna de otro estatus.
   */
  async moveSelectionToState (columnKey) {
    return this.updateTasksState(this.selection.ids, storedStateFor(columnKey) || 'created')
  },

  /**
   * Reasigna tareas arrastrándolas a la columna de otro usuario (vista Por
   * Usuario). Misma regla del backend que "Reasignar": mismo árbol y misma
   * ubicación del destino.
   */
  async reassignTasks (ids, userId) {
    const { data } = await ControlTowerService.bulkReassign({ ids, assigned_to: userId })
    this.dropFromSelection(ids)
    await this.search()
    return data
  },

  /**
   * Quita de la selección las tareas que ya se movieron, sin apagar el modo
   * "Seleccionar varias" ni tirar el resto de la selección.
   */
  dropFromSelection (ids) {
    const moved = new Set(ids)
    const remaining = this.selection.ids.filter(id => !moved.has(id))
    this.selection = {
      active: this.selection.active,
      columnKey: remaining.length ? this.selection.columnKey : null,
      ids: remaining
    }
  },

  // --- Mis Filtros ---------------------------------------------------------

  async fetchSavedFilters () {
    try {
      const { data } = await ControlTowerService.fetchFilters({ view_type: this.viewType })
      this.savedFilters = data || []
    } catch (error) {
      console.error(error)
    }
  },

  async saveFilter ({ name, overwrite = false }) {
    const payload = toSavedFilterPayload(this.filters, { name, viewType: this.viewType, overwrite })
    const { data } = await ControlTowerService.saveFilter(payload)
    await this.fetchSavedFilters()
    this.activeSavedFilterId = data.id

    return data
  },

  async renameFilter (id, name) {
    await ControlTowerService.updateFilter(id, { name, view_type: this.viewType })
    await this.fetchSavedFilters()
  },

  async deleteFilter (id) {
    await ControlTowerService.deleteFilter(id)
    if (this.activeSavedFilterId === id) this.activeSavedFilterId = null
    await this.fetchSavedFilters()
  },

  async applySavedFilter (record) {
    this.filters = fromSavedFilter(record)
    this.activeSavedFilterId = record ? record.id : null
    await this.search()
  },

  async resetFilters () {
    this.filters = emptyFilters()
    this.activeSavedFilterId = null
    this.focusedColumnKey = null
    await this.search()
  },

  // --- Preferencias de columnas -------------------------------------------

  async loadPreferences () {
    try {
      const { data } = await ControlTowerService.fetchPreferences({ view_type: this.viewType })
      const payload = data.payload || {}

      this.columnPreferences = {
        pinned: payload.pinned || [],
        sentToEnd: payload.sentToEnd || [],
        order: payload.order || []
      }
      this.listColumns = resolveListColumns(payload.listColumns, this.viewType)
    } catch (error) {
      console.error(error)
    }
  },

  async persistPreferences () {
    try {
      await ControlTowerService.savePreferences({
        view_type: this.viewType,
        payload: { ...this.columnPreferences, listColumns: this.listColumns }
      })
    } catch (error) {
      console.error(error)
    }
  },

  async pinColumn (key) {
    this.columnPreferences = pinColumn(this.columnPreferences, key)
    await this.persistPreferences()
  },

  async sendColumnToEnd (key) {
    this.columnPreferences = sendColumnToEnd(this.columnPreferences, key)
    await this.persistPreferences()
  },

  async swapColumns (keyA, keyB) {
    this.columnPreferences = swapColumns(this.columnPreferences, this.board.columns, keyA, keyB)
    await this.persistPreferences()
  },

  async setListColumns (keys) {
    this.listColumns = resolveListColumns(keys, this.viewType)
    await this.persistPreferences()
  },

  // --- Detalle -------------------------------------------------------------

  async openTask (taskId) {
    this.openedTaskId = taskId
    this.openedTask = null
    this.isLoadingTask = true

    try {
      const { data } = await ControlTowerService.fetchTaskDetail(taskId)
      this.openedTask = data
    } catch (error) {
      console.error(error)
      this.openedTask = null
    } finally {
      this.isLoadingTask = false
    }
  },

  closeTask () {
    this.openedTaskId = null
    this.openedTask = null
  },

  /**
   * Alta desde Torre de Control. Devuelve cuántas se crearon: en el flujo de
   * múltiples ubicaciones es una por ubicación y responsable.
   */
  async createTask (payload) {
    const { data } = await ControlTowerService.createTask(payload)
    await this.search()
    return data
  }
}
